import React, { useEffect, useState, useCallback } from 'react';
import { Phone, Bot, Mic, Clock, MessageSquare, Shield, CheckCircle, AlertCircle, Settings, Zap, Activity, Copy, Check, Plug } from 'lucide-react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const VOICES = [
  { value: 'alloy', label: 'Alloy', desc: 'Neutral, balanced' },
  { value: 'echo', label: 'Echo', desc: 'Warm, conversational' },
  { value: 'fable', label: 'Fable', desc: 'Expressive, British' },
  { value: 'onyx', label: 'Onyx', desc: 'Deep, authoritative' },
  { value: 'nova', label: 'Nova', desc: 'Friendly, energetic' },
  { value: 'shimmer', label: 'Shimmer', desc: 'Clear, professional' },
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

import { Section, Toggle, Field, INPUT } from './callingAgentUi';
import CallFlowSections from './CallFlowSections';
import WebTestCall from './WebTestCall';


/**
 * A read-only value that exists to be pasted somewhere else — here, into the
 * Exotel dashboard. Selects itself on focus so it stays usable when the
 * clipboard API is unavailable (denied permission, or a non-secure origin).
 */
function CopyField({ label, value, hint }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard denied — the input is still selectable, so manual copy works.
    }
  };

  return (
    <Field label={label}>
      <div className="flex items-start gap-2">
        <input
          readOnly
          value={value || ''}
          onFocus={e => e.target.select()}
          className={`${INPUT} font-mono text-[12px]`}
        />
        <button
          type="button"
          onClick={copy}
          title="Copy" aria-label="Copy"
          className="flex-shrink-0 p-2 border border-[#D8DDE6] rounded-[4px] text-[#706E6B] hover:text-[#0176D3] hover:border-[#0176D3] transition-colors"
        >
          {copied ? <Check size={15} className="text-[#04844B]" /> : <Copy size={15} />}
        </button>
      </div>
      {hint && <div className="text-[12px] text-[#706E6B] mt-1">{hint}</div>}
    </Field>
  );
}

// How each webhook outcome should read at a glance. 'stream_json' means the AI
// media stream was handed to Exotel (the healthy path); 'ivr_xml' means the call
// fell through to the IVR because the agent toggles are off; 'error_xml' means we
// answered with a hangup.
const OUTCOME_STYLES = {
  stream_json: { label: 'AI stream', className: 'bg-[#EBF7E6] border-[#CDEFC4] text-[#22683E]' },
  ivr_xml: { label: 'IVR fallback', className: 'bg-[#FEF1E5] border-[#FDE4C9] text-[#A96404]' },
  gather_xml: { label: 'Gather', className: 'bg-[#EEF4FF] border-[#D8E6FE] text-[#014486]' },
  hangup_xml: { label: 'Hangup', className: 'bg-[#F3F3F3] border-[#D8DDE6] text-[#706E6B]' },
  error_xml: { label: 'Error', className: 'bg-[#FEF1EE] border-[#FDDDE3] text-[#8E030F]' },
};

function WebhookHit({ hit }) {
  const outcome = OUTCOME_STYLES[hit.responseKind] || { label: hit.responseKind || 'unknown', className: 'bg-[#F3F3F3] border-[#D8DDE6] text-[#706E6B]' };
  const at = hit.at ? new Date(hit.at).toLocaleString() : '';
  return (
    <div className="border border-[#D8DDE6] rounded-[4px] p-3 space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-[3px] border ${outcome.className}`}>{outcome.label}</span>
        <span className="text-[12px] font-semibold text-[#080707]">{hit.method} /{hit.endpoint}</span>
        <span className="text-[11px] text-[#706E6B] ml-auto">{at}</span>
      </div>
      {hit.detail && <div className="text-[12px] text-[#706E6B] break-words">{hit.detail}</div>}
      {hit.params && Object.keys(hit.params).length > 0 && (
        <details className="text-[11px] text-[#706E6B]">
          <summary className="cursor-pointer select-none">What Exotel sent</summary>
          <pre className="mt-1 overflow-x-auto bg-[#F3F3F3] rounded-[3px] p-2">{JSON.stringify(hit.params, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}

export default function CallingAgentPage() {
  const [config, setConfig] = useState(null);
  const [exotelConfigured, setExotelConfigured] = useState(false);
  const [sarvamConfigured, setSarvamConfigured] = useState(false);
  const [exotelSetup, setExotelSetup] = useState(null);
  const [readiness, setReadiness] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [hits, setHits] = useState(null);
  const [loadingHits, setLoadingHits] = useState(false);
  const [hitsError, setHitsError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/calling-agent/config');
      setConfig(data.data);
      setExotelConfigured(data.exotelConfigured);
      setSarvamConfigured(data.sarvamConfigured);
      setExotelSetup(data.exotelSetup || null);
      setReadiness(data.readiness || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (path, value) => {
    setConfig(prev => {
      const next = { ...prev };
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = { ...obj[parts[i]] };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/calling-agent/config', config);
      setConfig(data.data);
      api.get('/calling-agent/config').then((r) => setReadiness(r.data.readiness || [])).catch(() => {});
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  // The server has no externally readable logs, so the only account of what
  // Exotel actually sent during a test call is this in-process buffer.
  const loadHits = async () => {
    setLoadingHits(true);
    setHitsError(null);
    try {
      const { data } = await api.get('/voice/exotel/diagnostics');
      setHits(data.data || []);
    } catch (err) {
      setHitsError(err.response?.data?.error || 'Could not load diagnostics');
    } finally {
      setLoadingHits(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/calling-agent/config/test');
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, error: err.response?.data?.error || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-10 px-4">
        <div className="text-[13px] text-[#706E6B]">Loading calling agent settings…</div>
      </div>
    );
  }

  const bh = config?.businessHours || {};
  const languageMode = config?.languageMode || 'en';
  const compliance = config?.compliance || {};
  const retryPolicy = compliance.retryPolicy || {};

  return (
    <div className={PAGE}>
      <PageHeader
        title="Calling Agent"
        icon={Phone}
        subtitle="Configure your AI voice agent. Each company has its own settings — the AI layer is provided by WaapLead."
        actions={<>
          {saved && (
            <span className="flex items-center gap-1 text-[12px] text-[#2E844A] font-medium">
              <CheckCircle size={14} /> Saved
            </span>
          )}
          <button onClick={save} disabled={saving} className="h-8 bg-[#0176D3] hover:bg-[#014486] disabled:opacity-60 text-white text-[13px] font-semibold px-3 rounded whitespace-nowrap">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>}
      />

      {readiness.length > 0 && (
        <Section title="Phone line readiness" icon={Phone}>
          <p className="text-[12px] text-[#706E6B]">Everything needed before the agent can place and answer real calls on your number.</p>
          <ul className="space-y-2">
            {readiness.map((i) => (
              <li key={i.key} className="flex items-start gap-2 text-[13px]">
                {i.ok ? <CheckCircle size={15} className="text-[#2E844A] mt-0.5 flex-shrink-0" /> : <AlertCircle size={15} className={`${i.required ? 'text-[#BA0517]' : 'text-[#706E6B]'} mt-0.5 flex-shrink-0`} />}
                <div>
                  <span className={i.ok ? 'text-[#080707]' : 'font-semibold text-[#080707]'}>{i.label}{!i.required && ' (optional)'}</span>
                  {!i.ok && <div className="text-[12px] text-[#706E6B]">{i.fix}</div>}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Try it in your browser" icon={Mic}>
        <WebTestCall />
      </Section>

      {/* Telephony provider status banner */}
      {!exotelConfigured && (
        <div className="flex items-start gap-3 bg-[#FEF1E5] border border-[#FDE4C9] rounded-[4px] px-4 py-3">
          <AlertCircle size={16} className="text-[#DD7A01] mt-0.5 flex-shrink-0" />
          <div className="text-[13px] text-[#A96404]">
            <strong>Exotel not configured.</strong> Add your Exotel SID, API Key/Token, and Caller ID under{' '}
            <a href="/dashboard/settings/integrations" className="underline font-semibold">Settings → Integrations</a>{' '}
            before enabling the calling agent.
          </div>
        </div>
      )}

      {/* Sarvam status banner — only relevant when a Hindi/Hinglish language mode is selected */}
      {languageMode !== 'en' && !sarvamConfigured && (
        <div className="flex items-start gap-3 bg-[#FEF1E5] border border-[#FDE4C9] rounded-[4px] px-4 py-3">
          <AlertCircle size={16} className="text-[#DD7A01] mt-0.5 flex-shrink-0" />
          <div className="text-[13px] text-[#A96404]">
            <strong>Hindi voice is not available yet.</strong> Hindi/Hinglish conversation mode depends on the platform's speech service, which has not been set up. Please contact support.
          </div>
        </div>
      )}

      {/* Master toggle */}
      <Section title="Agent Status" icon={Zap}>
        <Toggle
          checked={!!config?.isActive}
          onChange={v => update('isActive', v)}
          label="Enable Calling Agent"
          description="When on, this AI agent handles calls according to the settings below. Exotel credentials must be configured."
        />
      </Section>

      {/* Telephony + conversation stack */}
      <Section title="Telephony & Conversation Stack" icon={Phone}>
        <Field label="Telephony Provider">
          <div className="flex items-center gap-2 rounded-[4px] border border-[#D8DDE6] bg-[#F3F6F9] px-3 py-2.5">
            <Phone size={14} className="text-[#0176D3]" />
            <span className="text-[13px] font-bold text-[#080707]">Exotel</span>
            <span className="text-[11px] text-[#706E6B]">— India-native, DLT/DND compliant</span>
          </div>
          <p className="text-[11px] text-[#706E6B] mt-1.5">Requires Exotel credentials under Settings → Integrations.</p>
        </Field>

        <Field label="Conversation Language">
          <select className={INPUT} value={languageMode} onChange={e => update('languageMode', e.target.value)}>
            <option value="en">English (OpenAI Realtime — lowest latency)</option>
            <option value="hi">Hindi (Sarvam AI)</option>
            <option value="hinglish_auto">Hindi + English mixed / Hinglish (Sarvam AI, auto-detect)</option>
          </select>
          <p className="text-[11px] text-[#706E6B] mt-1.5">
            Which AI stack handles the live conversation. English keeps the existing low-latency setup; Hindi/Hinglish
            uses a Sarvam-based pipeline built for genuine code-switching. Requires a Sarvam API key for the latter two.
          </p>
        </Field>
      </Section>

      {/* Identity */}
      <Section title="Agent Identity" icon={Bot}>
        <Field label="Agent Name">
          <input className={INPUT} value={config?.agentName || ''} onChange={e => update('agentName', e.target.value)} placeholder="AI Assistant" />
          <p className="text-[11px] text-[#706E6B] mt-1">This name is used internally and in call logs.</p>
        </Field>

        <Field label="AI Voice">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {VOICES.map(v => (
              <button
                key={v.value}
                type="button"
                onClick={() => update('agentVoice', v.value)}
                className={`text-left rounded border px-3 py-3 transition-colors ${config?.agentVoice === v.value ? 'border-[#0176D3] bg-[#E3F2FF]' : 'border-[#D8DDE6] hover:border-[#0176D3]'}`}
              >
                <div className="text-[13px] font-bold text-[#080707]">{v.label}</div>
                <div className="text-[11px] text-[#706E6B]">{v.desc}</div>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[#706E6B] mt-1.5">Powered by OpenAI — WaapLead provides the AI. Your company chooses the voice.</p>
        </Field>

        <Field label="IVR / Static Prompt Language">
          <select className={INPUT} value={config?.language || 'en-US'} onChange={e => update('language', e.target.value)}>
            <option value="en-US">English (US)</option>
            <option value="en-GB">English (UK)</option>
            <option value="en-IN">English (India)</option>
            <option value="hi-IN">Hindi</option>
            <option value="es-ES">Spanish</option>
            <option value="fr-FR">French</option>
            <option value="ar-SA">Arabic</option>
          </select>
          <p className="text-[11px] text-[#706E6B] mt-1">
            Voice/locale for non-AI prompts (IVR menus, outbound scripts). This is separate from "Conversation Language" above,
            which controls the live AI conversation.
          </p>
        </Field>
      </Section>

      {/* Behavior */}
      <Section title="Behavior" icon={Mic}>
        <Field label="Welcome Message">
          <textarea
            className={`${INPUT} resize-none`}
            rows={2}
            value={config?.welcomeMessage || ''}
            onChange={e => update('welcomeMessage', e.target.value)}
            placeholder="Hello! Thank you for calling. How can I help you today?"
          />
          <p className="text-[11px] text-[#706E6B] mt-1">The AI will say this exactly when a call connects.</p>
        </Field>

        <Field label="AI System Prompt">
          <textarea
            className={`${INPUT} resize-none`}
            rows={4}
            value={config?.systemPrompt || ''}
            onChange={e => update('systemPrompt', e.target.value)}
            placeholder="You are a helpful AI voice assistant for [Your Company]. Be concise and professional…"
          />
          <p className="text-[11px] text-[#706E6B] mt-1">
            Define your agent's personality, knowledge scope, and rules. Keep it short — voice AI works best with focused prompts.
          </p>
        </Field>
      </Section>

      <CallFlowSections config={config} update={update} />

      {/* Inbound routing */}
      <Section title="Inbound Call Routing" icon={Phone}>
        <Toggle
          checked={!!config?.inboundEnabled}
          onChange={v => update('inboundEnabled', v)}
          label="Handle Inbound Calls"
          description="When on, incoming calls to your Exotel number are handled by this agent."
        />

        {config?.inboundEnabled && (
          <Toggle
            checked={!!config?.aiAnswersInbound}
            onChange={v => update('aiAnswersInbound', v)}
            label="AI Answers Directly"
            description="On = AI voice agent answers immediately. Off = IVR/keypad menu before any AI interaction."
          />
        )}

        <Toggle
          checked={config?.aiMakesOutbound !== false}
          onChange={v => update('aiMakesOutbound', v)}
          label="AI Talks on Outbound Calls"
          description="On = when someone picks up an outbound call, this agent talks to them. Off = a short spoken script with a keypad menu."
        />

        <Field label="Keep call recording links for (days, 0 = forever)">
          <input type="number" min={0} className={INPUT} value={config?.recordingRetentionDays ?? 90} onChange={e => update('recordingRetentionDays', Number(e.target.value))} />
        </Field>

        <div className="border-t border-[#D8DDE6] pt-4">
          <Toggle
            checked={!!config?.transferEnabled}
            onChange={v => update('transferEnabled', v)}
            label="Enable Human Transfer"
            description="AI can transfer the caller to a human agent when needed."
          />

          {config?.transferEnabled && (
            <div className="mt-3">
              <Field label="Transfer Phone Number">
                <input className={INPUT} value={config?.transferPhoneNumber || ''} onChange={e => update('transferPhoneNumber', e.target.value)} placeholder="+14155551234" />
              </Field>
              <p className="text-[11px] text-[#706E6B] mt-1">
                Trigger words: {(config?.transferTriggerWords || []).join(', ')}
              </p>
            </div>
          )}
        </div>
      </Section>

      {/* Missed-call WhatsApp */}
      <Section title="Missed-Call Recovery" icon={MessageSquare}>
        <Toggle
          checked={!!config?.missedCallWhatsAppEnabled}
          onChange={v => update('missedCallWhatsAppEnabled', v)}
          label="WhatsApp Follow-up on Missed Calls"
          description="Automatically send a WhatsApp message when an inbound call is missed or unanswered. Requires WhatsApp channel configured."
        />

        {config?.missedCallWhatsAppEnabled && (
          <Field label="Message Template">
            <textarea
              className={`${INPUT} resize-none`}
              rows={2}
              value={config?.missedCallWhatsAppMessage || ''}
              onChange={e => update('missedCallWhatsAppMessage', e.target.value)}
              placeholder="Hi {{name}}, we missed your call. We'll get back to you shortly."
            />
            <p className="text-[11px] text-[#706E6B] mt-1">Use {'{{name}}'} as a placeholder for the caller's phone number.</p>
          </Field>
        )}
      </Section>

      {/* Business hours */}
      <Section title="Business Hours" icon={Clock}>
        <Toggle
          checked={!!bh.enabled}
          onChange={v => update('businessHours.enabled', v)}
          label="Restrict to Business Hours"
          description="Outside business hours, the agent plays an after-hours message instead of answering."
        />

        {bh.enabled && (
          <>
            <Field label="Timezone">
              <select className={INPUT} value={bh.timezone || 'UTC'} onChange={e => update('businessHours.timezone', e.target.value)}>
                <option value="UTC">UTC</option>
                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                <option value="America/New_York">America/New_York (EST)</option>
                <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                <option value="Europe/London">Europe/London (GMT)</option>
                <option value="Europe/Paris">Europe/Paris (CET)</option>
                <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
              </select>
            </Field>

            <div className="space-y-2">
              {DAYS.map(day => {
                const d = bh[day] || {};
                return (
                  <div key={day} className="flex items-center gap-3">
                    <div className="flex items-center gap-2 w-32">
                      <button
                        type="button"
                        onClick={() => update(`businessHours.${day}.enabled`, !d.enabled)}
                        className={`relative inline-flex h-4 w-8 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${d.enabled ? 'bg-[#0176D3]' : 'bg-[#D8DDE6]'}`}
                      >
                        <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transition-transform ${d.enabled ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                      <span className="text-[12px] font-semibold text-[#080707] capitalize">{day.slice(0, 3)}</span>
                    </div>
                    {d.enabled ? (
                      <div className="flex items-center gap-2">
                        <input type="time" className="border border-[#D8DDE6] rounded-[4px] px-2 py-1 text-[12px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none" value={d.open || '09:00'} onChange={e => update(`businessHours.${day}.open`, e.target.value)} />
                        <span className="text-[12px] text-[#706E6B]">to</span>
                        <input type="time" className="border border-[#D8DDE6] rounded-[4px] px-2 py-1 text-[12px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none" value={d.close || '18:00'} onChange={e => update(`businessHours.${day}.close`, e.target.value)} />
                      </div>
                    ) : (
                      <span className="text-[12px] text-[#706E6B]">Closed</span>
                    )}
                  </div>
                );
              })}
            </div>

            <Field label="After-Hours Message">
              <textarea
                className={`${INPUT} resize-none`}
                rows={2}
                value={bh.afterHoursMessage || ''}
                onChange={e => update('businessHours.afterHoursMessage', e.target.value)}
                placeholder="We're currently closed. Please call back during business hours."
              />
            </Field>
          </>
        )}
      </Section>

      {/* Compliance */}
      <Section title="Compliance" icon={Shield}>
        <Field label="Consent Announcement">
          <textarea
            className={`${INPUT} resize-none`}
            rows={2}
            value={compliance.consentAnnouncement || ''}
            onChange={e => update('compliance.consentAnnouncement', e.target.value)}
            placeholder="This call may be recorded for quality and training purposes."
          />
          <p className="text-[11px] text-[#706E6B] mt-1">Said verbatim before the welcome message on every call. Leave blank to skip.</p>
        </Field>

        <Field label="Max Call Duration (seconds)">
          <input
            type="number"
            min="30"
            className={INPUT}
            value={compliance.maxCallDurationSeconds ?? 600}
            onChange={e => update('compliance.maxCallDurationSeconds', Number(e.target.value))}
          />
          <p className="text-[11px] text-[#706E6B] mt-1">Calls are automatically ended after this long, regardless of AI behavior.</p>
        </Field>

        <Toggle
          checked={compliance.enforceCallingWindow !== false}
          onChange={v => update('compliance.enforceCallingWindow', v)}
          label="Enforce Calling Window (9 AM–9 PM)"
          description="Blocks outbound calls to Indian numbers outside 9 AM–9 PM in your configured timezone, per TRAI regulations."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Max Retry Attempts">
            <input
              type="number"
              min="1"
              className={INPUT}
              value={retryPolicy.maxAttempts ?? 3}
              onChange={e => update('compliance.retryPolicy.maxAttempts', Number(e.target.value))}
            />
          </Field>
          <Field label="Retry Backoff (minutes)">
            <input
              type="number"
              min="1"
              className={INPUT}
              value={retryPolicy.backoffMinutes ?? 120}
              onChange={e => update('compliance.retryPolicy.backoffMinutes', Number(e.target.value))}
            />
          </Field>
        </div>
        <p className="text-[11px] text-[#706E6B]">
          Limits how many times and how often the same number can be re-dialed. Contacts marked "Do Not Contact" are always blocked.
        </p>
      </Section>

      {/* The values that have to be pasted into Exotel's App Bazaar flow. They
          live here because the panel is the only place that knows both this
          tenant's companyId and the origin the server actually advertises to
          Exotel — previously this meant reading an API response by hand. */}
      <Section title="Exotel Flow Setup" icon={Plug}>
        {!exotelSetup ? (
          <div className="text-[13px] text-[#706E6B]">Loading setup details…</div>
        ) : (
          <>
            <div className="text-[13px] text-[#706E6B]">
              In Exotel → App Bazaar, build a flow whose first applet is a <strong>Voicebot Applet</strong> in
              dynamic-URL mode. Paste the URL below into it (method <strong>GET</strong>, media format{' '}
              <strong>8000 Hz</strong>), then attach that flow to your Exophone&apos;s incoming calls.
            </div>

            {exotelSetup.panelOrigin && !exotelSetup.originMatchesPanel && (
              <div className="flex items-start gap-2 rounded-[4px] bg-[#FEF1E5] border border-[#FDE4C9] px-4 py-3 text-[12px] text-[#A96404]">
                <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Host mismatch.</strong> You are viewing this panel on{' '}
                  <span className="font-mono">{exotelSetup.panelOrigin}</span>, but this server advertises{' '}
                  <span className="font-mono">{exotelSetup.publicBaseUrl}</span> to Exotel (its{' '}
                  <span className="font-mono">PUBLIC_BASE_URL</span>). The URLs below use the latter, because that
                  is what Exotel is actually handed. If that host is wrong, calls will connect and then drop — fix{' '}
                  <span className="font-mono">PUBLIC_BASE_URL</span> on the server before configuring the flow.
                </div>
              </div>
            )}

            <CopyField
              label="Voicebot Applet URL (dynamic URL mode)"
              value={exotelSetup.voicebotAppletUrl}
              hint="Preferred. The server answers every call with a fresh media-stream address, so nothing per-call is ever typed by hand."
            />

            {config?.transferEnabled && (
              <CopyField
                label="Transfer URL (add a Passthru/Connect applet AFTER the Voicebot applet)"
                value={exotelSetup.transferAppletUrl}
                hint="When the AI puts a caller through to a person, the Voicebot stream closes and the call flow moves to the next applet. Point that applet here and it dials your transfer number; any other call just ends."
              />
            )}

            <CopyField
              label="WebSocket URL (only if the applet needs a fixed address)"
              value={exotelSetup.webSocketUrl}
              hint="Use this only when the applet offers no dynamic-URL mode. It carries no per-call values, so call-log linkage and repeat-caller memory are lost — the URL above keeps both."
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CopyField
                label="Company ID"
                value={exotelSetup.companyId}
                hint="Already embedded in both URLs above."
              />
              <CopyField
                label="Number to call for an inbound test"
                value={exotelSetup.exophone || ''}
                hint={exotelSetup.exophone
                  ? 'Your Exotel Exophone, as saved under Integrations.'
                  : 'No Exophone saved yet — add your Exotel caller ID under Settings → Integrations.'}
              />
            </div>
          </>
        )}
      </Section>

      {/* Connection test */}
      <Section title="Connection Test" icon={Settings}>
        <div className="flex items-start gap-4">
          <div className="flex-1 text-[13px] text-[#706E6B]">
            Verify that your Exotel credentials are valid and the WaapLead AI layer is reachable before enabling the agent.
          </div>
          <button
            onClick={test}
            disabled={testing}
            className="flex-shrink-0 border border-[#D8DDE6] hover:border-[#0176D3] text-[13px] font-semibold text-[#080707] px-4 py-2 rounded whitespace-nowrap transition-colors disabled:opacity-60"
          >
            {testing ? 'Testing…' : 'Run Test'}
          </button>
        </div>

        {testResult && (
          <div className={`flex items-start gap-2 rounded-[4px] px-4 py-3 text-[13px] ${testResult.ok ? 'bg-[#EBF7E6] border border-[#CDEFC4] text-[#22683E]' : 'bg-[#FEF1EE] border border-[#FDDDE3] text-[#8E030F]'}`}>
            {testResult.ok ? <CheckCircle size={15} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />}
            <div>
              <strong>{testResult.ok ? 'Ready to take calls.' : 'Not ready to take calls.'}</strong>
              {/* Rendered whether or not the overall test passed: a failure is
                  exactly when the per-piece breakdown is worth reading, and
                  hiding it behind `ok` meant a failed test showed one summary
                  line and no indication of which piece was missing. */}
              {testResult.details && (
                <div className="mt-1 space-y-0.5 text-[12px]">
                  <div className="capitalize">{testResult.details.telephonyProvider || 'exotel'}: {testResult.details.telephonyConnected ? '✓ Connected' : '✗ Not connected'} {testResult.details.phoneNumber ? `(${testResult.details.phoneNumber})` : ''}</div>
                  {(testResult.details.speechChecks || []).map(check => (
                    <div key={check.name}>
                      {check.name}: {check.ok ? '✓ Ready' : `✗ ${check.error || 'Not configured'}`}
                    </div>
                  ))}
                </div>
              )}
              {!testResult.ok && !testResult.details && (
                <div className="mt-1 text-[12px]">{testResult.error}</div>
              )}
            </div>
          </div>
        )}
      </Section>

      <Section title="Inbound Call Diagnostics" icon={Activity}>
        <div className="flex items-start gap-4">
          <div className="flex-1 text-[13px] text-[#706E6B]">
            The most recent webhook requests Exotel made to this server, and how each one was
            answered. Place a test call, then refresh — if a call connects and drops, the reason
            appears here. The list is held in memory and clears when the server restarts.
          </div>
          <button
            onClick={loadHits}
            disabled={loadingHits}
            className="flex-shrink-0 border border-[#D8DDE6] hover:border-[#0176D3] text-[13px] font-semibold text-[#080707] px-4 py-2 rounded whitespace-nowrap transition-colors disabled:opacity-60"
          >
            {loadingHits ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {hitsError && (
          <div className="flex items-start gap-2 rounded-[4px] px-4 py-3 text-[13px] bg-[#FEF1EE] border border-[#FDDDE3] text-[#8E030F]">
            <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />
            <div>{hitsError}</div>
          </div>
        )}

        {hits && hits.length === 0 && (
          <div className="flex items-start gap-2 rounded-[4px] px-4 py-3 text-[13px] bg-[#FEF1E5] border border-[#FDE4C9] text-[#A96404]">
            <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />
            <div>
              <strong>Exotel has not called this server.</strong> If a test call was just placed, the
              call flow in your Exotel dashboard is not pointed at this CRM — check the applet URL
              before looking anywhere else.
            </div>
          </div>
        )}

        {hits && hits.length > 0 && (
          <div className="space-y-2">
            {hits.map((hit, i) => <WebhookHit key={`${hit.at}-${i}`} hit={hit} />)}
          </div>
        )}
      </Section>

      {/* Save footer */}
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="bg-[#0176D3] hover:bg-[#0153A0] disabled:opacity-60 text-white text-[13px] font-semibold px-6 py-2 rounded whitespace-nowrap">
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
